import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

/**
 * ⭐ useCartStore - Global shopping cart state
 *
 * Uses Immer middleware for easy nested state updates
 *
 * State:
 *  - cart: Array of items {_id, name, price, quantity, image, ...}
 *  - lastUpdated: Timestamp of last update (for sync tracking)
 *
 * Actions:
 *  - addToCart: Add item or increase quantity
 *  - removeFromCart: Remove item by _id
 *  - updateQuantity: Update quantity of specific item
 *  - clearCart: Empty cart
 *  - getTotal: Calculate total price (DERIVED STATE - not persisted)
 *  - getItemCount: Get number of items in cart
 *
 * ⚠️ Derived State Note:
 * totalPrice is NOT stored in state. Calculate on-the-fly with getTotal()
 * This prevents stale data in localStorage
 */

const useCartStore = create(
  immer(
    persist(
      (set, get) => ({
        // ============ STATE ============
        cart: [],
        lastUpdated: null,

        // ============ ACTIONS ============

        /**
         * Add product to cart or increase quantity if exists
         * @param {Object} product - Product object with _id from MongoDB
         * @param {Number} quantity - Quantity to add (default: 1)
         */
        addToCart: (product, quantity = 1) =>
          set((state) => {
            // With Immer, we can mutate state directly!
            const existingItem = state.cart.find(
              (item) => item._id === product._id,
            );

            if (existingItem) {
              // Item exists - just increase quantity
              existingItem.quantity += quantity;
            } else {
              // New item - add to cart
              state.cart.push({
                ...product,
                quantity,
              });
            }
            state.lastUpdated = Date.now();
          }),

        /**
         * Remove item from cart by MongoDB _id
         * @param {String} productId - MongoDB _id
         */
        removeFromCart: (productId) =>
          set((state) => {
            state.cart = state.cart.filter((item) => item._id !== productId);
            state.lastUpdated = Date.now();
          }),

        /**
         * Remove multiple items from cart by their MongoDB _ids
         * @param {Array<String>} productIds - Array of MongoDB _ids
         */
        removeItems: (productIds) =>
          set((state) => {
            state.cart = state.cart.filter(
              (item) => !productIds.includes(item._id),
            );
            state.lastUpdated = Date.now();
          }),

        /**
         * Update quantity of specific item
         * @param {String} productId - MongoDB _id
         * @param {Number} newQuantity - New quantity (remove if <= 0)
         */
        updateQuantity: (productId, newQuantity) =>
          set((state) => {
            const item = state.cart.find((item) => item._id === productId);

            if (item) {
              if (newQuantity <= 0) {
                // Remove if quantity is 0 or less
                state.cart = state.cart.filter((i) => i._id !== productId);
              } else {
                item.quantity = newQuantity;
              }
              state.lastUpdated = Date.now();
            }
          }),

        /**
         * Clear all items from cart
         */
        clearCart: () =>
          set((state) => {
            state.cart = [];
            state.lastUpdated = Date.now();
          }),

        /**
         * Calculate total price (DERIVED STATE - not stored)
         * Call this in components to get current total
         * @returns {Number} Total price
         */
        getTotal: () => {
          const state = get();
          return state.cart.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0,
          );
        },

        /**
         * Get total number of items in cart
         * @returns {Number} Item count
         */
        getItemCount: () => {
          const state = get();
          return state.cart.reduce((count, item) => count + item.quantity, 0);
        },
      }),
      {
        name: "cart-storage", // localStorage key
        partialize: (state) => ({
          cart: state.cart,
          // ⚠️ lastUpdated: NOT persisted (transient state)
          // ⚠️ totalPrice: NOT stored (it's derived!)
        }),
      },
    ),
  ),
);

export default useCartStore;
